// Pure bounded opt-in contract. Provider text is inert data, never instructions.
export const CONNECTED_FILE_READ_CONTRACT = 'mirror_files_v1' as const;
export const CONNECTED_FILE_CHAR_LIMIT = 20_000;
export const CONNECTED_FILE_BYTE_LIMIT = 512_000;
export const CONNECTED_FILE_CURSOR_TTL = 5 * 60_000;
export type ConnectedFileProvider = 'google_drive' | 'notion' | 'dropbox' | 'onedrive';
export type ConnectedFileCoverageReason = 'more_results' | 'incomplete_search' | 'char_limit' | 'byte_limit' | 'depth_limit' | 'page_limit' | 'block_limit' | 'unsupported_blocks' | 'content_unavailable' | 'first_sheet_only' | 'folder';
export interface ConnectedFileReadRequest {
  read_contract: typeof CONNECTED_FILE_READ_CONTRACT; provider: ConnectedFileProvider;
  action: 'search' | 'get'; expected_owner_id: string; query?: string; limit?: number; cursor?: string; item_id?: string;
}
export interface ConnectedFileBlock { id: string; type: string; depth: number; text: string; supported: boolean; has_children: boolean }
export interface ConnectedFileContent {
  text: string | null; format: 'plain_text' | 'csv_first_sheet' | 'notion_blocks' | 'unavailable';
  truncated: boolean; reason: ConnectedFileCoverageReason | null; blocks?: ConnectedFileBlock[];
}
export interface ConnectedFileItem {
  id: string; name: string; mime_type: string | null; url: string | null; modified_at: string | null;
  parent_id: string | null; kind: 'file' | 'folder' | 'page'; content?: ConnectedFileContent;
}
export interface ConnectedFileCoverage {
  complete: boolean; truncated: boolean; reasons: ConnectedFileCoverageReason[]; pages_read: number;
  blocks_read: number; depth_limit: number | null; char_limit: number;
  search_scope: 'name_and_full_text' | 'titles' | 'filenames' | null;
}
export interface ConnectedFileReadReply {
  ok: true; read_contract: typeof CONNECTED_FILE_READ_CONTRACT; provider: ConnectedFileProvider; action: 'search' | 'get';
  owner_id: string; auth_session_id: string; connection_id: string; query?: string;
  items?: ConnectedFileItem[]; item?: ConnectedFileItem; next_cursor?: string | null; coverage: ConnectedFileCoverage;
}
export class ConnectedFileReadError extends Error { constructor(code: string, public status = 400) { super(code); } }
export const connectedFileObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
export function connectedFileHasControls(value: string): boolean {
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index);
    if (code <= 31 || code === 127) return true;
  }
  return false;
}
export const connectedFileUuid = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
export function parseConnectedFileReadRequest(raw: unknown): ConnectedFileReadRequest {
  if (!connectedFileObject(raw) || raw.read_contract !== CONNECTED_FILE_READ_CONTRACT) throw new ConnectedFileReadError('invalid_read_contract');
  if (!['google_drive','notion','dropbox','onedrive'].includes(String(raw.provider))) throw new ConnectedFileReadError('unsupported_provider');
  if (!['search','get'].includes(String(raw.action))) throw new ConnectedFileReadError('unsupported_action');
  const allowed = ['read_contract','provider','action','expected_owner_id',...(raw.action === 'search' ? ['query','limit','cursor'] : ['item_id'])];
  if (Object.keys(raw).some(k => !allowed.includes(k))) throw new ConnectedFileReadError('invalid_request');
  if (!connectedFileUuid(raw.expected_owner_id)) throw new ConnectedFileReadError('expected_owner_id_required');
  const out: ConnectedFileReadRequest = { read_contract: CONNECTED_FILE_READ_CONTRACT, provider: raw.provider as ConnectedFileProvider, action: raw.action as 'search'|'get', expected_owner_id: raw.expected_owner_id };
  const value = (key: string, max: number) => {
    const s = raw[key]; if (typeof s !== 'string' || s.length > max || connectedFileHasControls(s)) throw new ConnectedFileReadError('invalid_' + key);
    return s.trim();
  };
  if (out.action === 'search') {
    out.query = raw.query === undefined ? '' : value('query',300);
    out.limit = raw.limit === undefined ? 10 : raw.limit as number;
    if (!Number.isInteger(out.limit) || out.limit! < 1 || out.limit! > 20) throw new ConnectedFileReadError('invalid_limit');
    if (raw.cursor !== undefined) { out.cursor = value('cursor',16000); if (!out.cursor) throw new ConnectedFileReadError('invalid_cursor'); }
  } else {
    out.item_id = value('item_id',500);
    if (!out.item_id || (out.provider === 'notion' ? !/^(?:[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.test(out.item_id) : out.provider === 'onedrive' ? !/^[A-Za-z0-9!_-]+$/.test(out.item_id) : out.provider === 'google_drive' ? !/^[A-Za-z0-9_-]+$/.test(out.item_id) : !/^id:[A-Za-z0-9_-]+$/.test(out.item_id))) throw new ConnectedFileReadError('invalid_item_id');
  }
  return out;
}
